/**
 * tax/LoanRequest.tsx — 임지도사 협업방 「정책자금 대출 요청하기」 (2026-10-09 지도사)
 *  2026-10-09 디자인 시안(zip 「정책자금 대출 요청」) 적용: 640 폭 · 「정책자금 대출, 몇 가지만 알려주세요」 · 단계 카드 1/4 ~ 4/4 · 아래 고정 버튼(남은 항목 수)
 *  ① 어떤 사업자(개인사업자 · 법인) ② 사업자 정보(사업자등록번호 · 대표자 주민번호 · 대표자 연락처)
 *  ③ 서류 — 필수 아님(「보유하신 서류만 먼저」) · 법인을 고르면 주주명부 칸이 열린다
 *  ④ 요청하는 분(성함 · 연락처 · 결과를 누구에게 먼저 알릴지 · 전달하고 싶은 말) + 개인정보 동의
 *  보내면 로움 일일 업무(평일 가장 가까운 날 · 담당 오재인 + 임우승)로 들어가고, 화면에는 「3일 이내 1차 답변」
 *  주민번호는 이 화면에 남기지 않는다(기억 안 함) · 요청자 성함 · 연락처는 소식지 「보내는 곳」에 저장한 값으로 미리 채움
 */
import React from 'react';
import { X, KInput, card, inputSt, useWide, Shell, Section, Dot } from './taxUi';
import { load, forget, OFFICE_KEY } from './taxApi';
import { digits, fmtBiz, phone, bizOk, kindOfBiz } from './taxForm';
import type { Progress } from './TaxApp';

interface Office { office: string; person: string; tel: string; mob: string }
const fmtRrn = (v: string) => { const d = digits(v, 13); return d.length <= 6 ? d : `${d.slice(0, 6)}-${d.slice(6)}`; };
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.xls,.xlsx,.hwp,.hwpx,.zip,image/*';
const MB = 1024 * 1024;
const MEMO_MAX = 500;
const NOTIFY: [('ceo' | 'referrer'), string, string][] = [['ceo', '대표님께 직접 연락', '대표자 연락처로 결과를 바로 알려드려요'], ['referrer', '소개자에게 먼저 알림', '요청하신 분께 먼저 결과를 알려드려요']];

type Step = 'kind' | 'biz' | 'req' | 'agree';

/** 고르기 카드(시안) — 개인/법인은 점이 오른쪽, 알림 받을 곳은 점이 왼쪽 */
export function Choice({ on, onClick, title, desc, dotLeft, big, tone, toneLt }: { on: boolean; onClick: () => void; title: string; desc: string; dotLeft?: boolean; big?: boolean; tone?: string; toneLt?: string }) {
  return (
    <button type="button" role="radio" aria-checked={on} onClick={onClick} style={{
      textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer', padding: dotLeft ? 16 : 18, borderRadius: 14, display: 'flex', alignItems: dotLeft ? 'center' : 'flex-start', gap: 12, transition: 'all .15s',
      border: `${on && big ? 2 : 1.5}px solid ${on ? (tone || X.blue) : big ? '#C4C9D1' : X.line}`, background: on ? (tone ? toneLt || X.card : X.blueLt) : X.card, flexDirection: dotLeft ? 'row-reverse' : 'row',
    }}>
      <span style={{ display: 'flex', flexDirection: 'column', gap: dotLeft ? 2 : 4, flex: 1 }}>
        <span style={{ fontSize: big ? 18 : dotLeft ? 15 : 16, fontWeight: dotLeft && !big ? 600 : 700, color: X.text }}>{title}</span>
        <span style={{ fontSize: big ? 15 : 13, color: big ? X.label : X.sub }}>{desc}</span>
      </span>
      <Dot on={on} tone={tone} size={big ? 26 : 22} />
    </button>
  );
}

/** 이름표 + 칸(시안: 이름표 14/600 #4B5058 · 간격 8) */
export function Field({ label, children, extra, big }: { label: string; children: React.ReactNode; extra?: React.ReactNode; big?: boolean }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: big ? 16 : 14, fontWeight: big ? 700 : 600, color: X.label }}>{label}</span>
      {children}{extra}
    </label>
  );
}

export default function LoanRequest({ onHome, onProgress }: { onHome: () => void; onProgress: (p: Progress | null) => void }) {
  const wide = useWide(768);
  const [saved, setSaved] = React.useState<Office>(() => load<Office>(OFFICE_KEY, { office: '', person: '', tel: '', mob: '' }));
  const [biz, setBiz] = React.useState('');
  const [rrn, setRrn] = React.useState('');
  const [showRrn, setShowRrn] = React.useState(false);
  const [ceoTel, setCeoTel] = React.useState('');
  const [fin, setFin] = React.useState<File[]>([]);
  const [holders, setHolders] = React.useState<File[]>([]);
  const [reqName, setReqName] = React.useState(saved.person || '');
  const [reqTel, setReqTel] = React.useState(saved.mob || saved.tel || '');
  const [notify, setNotify] = React.useState<'' | 'ceo' | 'referrer'>('');
  const [memo, setMemo] = React.useState('');
  const [agree, setAgree] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState<{ replyBy: string } | null>(null);
  const refs = React.useRef<Partial<Record<Step, HTMLElement | null>>>({});

  // 필수 묶음 3개(서류는 필수 아님) — 머리줄 진행 표시 · 버튼 문구
  const kind = bizOk(biz) ? kindOfBiz(biz) : '';   // 개인 · 법인은 묻지 않고 사업자번호로(2026-10-14 지도사 「사업자번호 받으면 알잖아」)
  const okKind = true;
  const okBiz = bizOk(biz) && digits(rrn, 13).length === 13 && digits(ceoTel, 11).length >= 9;
  const okReq = !!reqName.trim() && digits(reqTel, 11).length >= 9 && !!notify;
  const left = [okKind, okBiz, okReq].filter((x) => !x).length;
  const ready = left === 0 && agree;
  React.useEffect(() => {
    onProgress(done ? { label: '접수 완료', pct: 100 } : { label: `${3 - left} / 3 필수 완료`, pct: Math.round(((3 - left) / 3) * 100) });
  }, [left, done, onProgress]);

  const jump = (k: Step) => {
    const el = refs.current[k];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const i = k === 'agree' ? el : el.querySelector('input:not([type=file]),textarea,button');
    if (i instanceof HTMLElement) window.setTimeout(() => i.focus({ preventScroll: true }), 300);
  };
  const submit = async () => {
    if (busy) return;
    if (!ready) { jump(!okKind ? 'kind' : !okBiz ? 'biz' : !okReq ? 'req' : 'agree'); return; }
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('kind', kind); fd.append('biz', digits(biz, 10)); fd.append('rrn', digits(rrn, 13)); fd.append('ceoTel', ceoTel);
      fd.append('reqName', reqName.trim()); fd.append('reqTel', reqTel); fd.append('office', saved.office || '');
      fd.append('notify', notify); fd.append('memo', memo.trim()); fd.append('agree', '1');
      fin.forEach((f) => fd.append('fin', f));
      if (kind === '법인') holders.forEach((f) => fd.append('holders', f));
      const r = await fetch('/api/tax/loan', { method: 'POST', body: fd });
      const j = await r.json().catch(() => null) as { success?: boolean; data?: { reply_by: string }; error?: string } | null;
      if (!r.ok || !j || !j.success || !j.data) throw new Error((j && j.error) || '접수하지 못했어요 — 잠시 뒤 다시 시도해 주세요');
      setRrn(''); setDone({ replyBy: j.data.reply_by });
      window.scrollTo(0, 0);
    } catch (e) { setMsg(e instanceof Error ? e.message : '접수하지 못했어요'); }
    finally { setBusy(false); }
  };
  const set = (k: Step) => (el: HTMLElement | null) => { refs.current[k] = el; };

  if (done) {
    const [, mm, dd] = done.replyBy.split('-').map(Number);
    return (
      <Done title="대출 요청을 보냈어요" onHome={onHome}>
        담당자가 서류를 검토한 뒤<br /><b style={{ color: X.text }}>3일 이내({mm}월 {dd}일까지)</b> {notify === 'ceo' ? '대표님 연락처' : reqTel}로 1차 답변을 드릴게요.
      </Done>
    );
  }

  return (
    <Shell onHome={onHome} title={<>정책자금 대출,<br />몇 가지만 알려주세요</>} sub="입력한 정보는 대출 검토에만 사용돼요." wide={wide} max={640}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 170 }}>
        <div ref={set('biz')}>
          <Section n={1} of={3} title="사업자 정보">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <Field label="사업자등록번호" extra={digits(biz, 10).length === 10 && !bizOk(biz) ? <span style={{ fontSize: 13, color: X.red }}>번호를 다시 확인해 주세요</span> : kind ? <span style={{ fontSize: 13, color: X.label }}>{kind === '법인' ? '법인사업자예요' : '개인사업자예요'}</span> : null}>
                <KInput inputMode="numeric" value={biz} placeholder="000-00-00000" onChange={(v) => setBiz(fmtBiz(v))} />
              </Field>
              <Field label="대표자 주민등록번호">
                <span style={{ position: 'relative', display: 'flex' }}>
                  <input inputMode="numeric" autoComplete="off" type={showRrn ? 'text' : 'password'} value={rrn} placeholder="000000-0000000"
                    onChange={(e) => setRrn(fmtRrn(e.target.value))} style={{ ...inputSt, flex: 1, minWidth: 0, paddingRight: 96 }} />
                  <button type="button" onClick={(e) => { e.preventDefault(); setShowRrn(!showRrn); }} style={{ position: 'absolute', right: 8, top: 9, height: 36, padding: '0 12px', border: 'none', borderRadius: 9, background: X.line, color: X.label, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{showRrn ? '숨기기' : '번호 보기'}</button>
                </span>
              </Field>
              <Field label="대표자 연락처">
                <input inputMode="tel" autoComplete="off" value={ceoTel} placeholder="010-0000-0000" onChange={(e) => setCeoTel(phone(e.target.value))} style={inputSt} />
              </Field>
            </div>
          </Section>
        </div>

        <Section n={2} of={3} title={kind === '법인' ? '재무제표 · 주주명부' : '직전 3개년 재무제표'} hint="PDF · 사진 · 엑셀 · 한글 파일을 여러 개 올릴 수 있어요.">
          {/* 서류는 있는 것만 먼저 받아도 된다(2026-10-09 지도사) — 필수 아님 */}
          <div style={{ fontSize: 14, color: X.blueDk, background: X.blueLt, borderRadius: 12, padding: '12px 14px', marginBottom: 16, lineHeight: 1.6 }}>
            모든 서류를 제출하지 않으셔도 됩니다. 보유하신 서류만 먼저 제출해 주세요.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <FilePick label="직전 3개년 재무제표" files={fin} max={6} onChange={setFin} />
            {kind === '법인' && <FilePick label="주주명부" files={holders} max={4} onChange={setHolders} />}
          </div>
        </Section>

        <div ref={set('req')}>
          <Section n={3} of={3} title="요청하는 분" hint="검토 결과를 이 연락처로 알려드려요.">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <SavedNote saved={saved} onForget={() => { forget(OFFICE_KEY); setSaved({ office: '', person: '', tel: '', mob: '' }); setReqName(''); setReqTel(''); }} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 }}>
                <Field label="이름"><KInput value={reqName} maxLength={20} placeholder="홍길동" autoComplete="name" onChange={setReqName} /></Field>
                <Field label="연락처"><input inputMode="tel" autoComplete="tel" value={reqTel} placeholder="010-0000-0000" onChange={(e) => setReqTel(phone(e.target.value))} style={inputSt} /></Field>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: X.label }}>검토 결과는 누구에게 먼저 알려드릴까요?</span>
                <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {NOTIFY.map(([k, t, d]) => <Choice key={k} on={notify === k} onClick={() => setNotify(k)} title={t} desc={d} dotLeft />)}
                </div>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 14, fontWeight: 600, color: X.label }}>임지도사에게 전달하고 싶은 말 <span style={{ fontWeight: 400, color: X.muted, whiteSpace: 'nowrap' }}>선택 · {memo.length} / {MEMO_MAX}</span></span>
                <textarea value={memo} maxLength={MEMO_MAX} rows={4} placeholder="예) 기존 대출 10억원 만기가 다가오는데, 대환대출로 금리를 낮출 수 있는지 알아봐 주세요."
                  onChange={(e) => setMemo(e.target.value.slice(0, MEMO_MAX))}
                  style={{ ...inputSt, height: 'auto', minHeight: 110, padding: '14px 16px', fontSize: 16, lineHeight: 1.55, resize: 'vertical' }} />
              </label>
            </div>
          </Section>
        </div>

        <Agree on={agree} onToggle={() => setAgree(!agree)} refEl={set('agree')} text="정책자금 대출 검토를 위해 대표자 주민등록번호·연락처와 제출 서류를 수집·이용해요." />

        {msg && <div role="alert" style={{ fontSize: 15, color: X.red, background: X.redLt, borderRadius: 12, padding: '12px 14px' }}>{msg}</div>}
      </div>

      <BottomCta note={<>접수하면 <b style={{ color: X.text }}>3일 이내</b> 1차 답변을 드려요</>} ready={ready} busy={busy} onClick={submit}
        label={busy ? '보내는 중…' : ready ? '대출 요청 보내기' : left ? `${left}개 항목을 더 채워주세요` : '동의 후 보낼 수 있어요'} />
    </Shell>
  );
}

/** 이 기기(브라우저)에만 저장해 둔 요청자 · 사무소 정보 안내 + 지우기 — 서버에는 남기지 않고 다른 사무소 기기에는 보이지 않는다 */
export function SavedNote({ saved, onForget }: { saved: { office: string; person: string; tel: string; mob: string }; onForget: () => void }) {
  if (!saved.office && !saved.person && !saved.mob && !saved.tel) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: X.sub, background: X.field, borderRadius: 10, padding: '10px 12px', marginTop: -4 }}>
      <span style={{ flex: 1, minWidth: 0 }}>이 기기에 저장해 둔 정보로 채웠어요{saved.office ? ` · ${saved.office}` : ''}</span>
      <button type="button" onClick={onForget} style={{ flex: 'none', border: 'none', background: X.card, borderRadius: 8, padding: '6px 10px', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: X.label, cursor: 'pointer' }}>다른 사람이면 지우기</button>
    </div>
  );
}

/** [필수] 개인정보 동의 카드(시안) — 카드 전체를 눌러 켜고 끈다 */
export function Agree({ on, onToggle, text, refEl, big, tone, title = '[필수] 개인정보 수집·이용 동의' }: { on: boolean; onToggle: () => void; text: string; refEl?: (el: HTMLElement | null) => void; big?: boolean; tone?: string; title?: string }) {
  return (
    <button type="button" ref={refEl} role="checkbox" aria-checked={on} onClick={onToggle} style={{ ...card, fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', border: big ? `${on ? 2 : 1.5}px solid ${on ? (tone || X.blue) : '#C4C9D1'}` : 'none', borderRadius: big ? 14 : 20, padding: big ? '16px 18px' : '20px 24px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <span style={{ marginTop: 1 }}><Dot on={on} size={big ? 26 : 24} tone={tone} /></span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: big ? 17 : 15, fontWeight: big ? 700 : 600, color: X.text }}>{title}</span>
        <span style={{ fontSize: big ? 15 : 13, color: big ? X.label : X.sub, lineHeight: 1.55 }}>{text}</span>
      </span>
    </button>
  );
}

/** 접수 완료 화면(시안) — 파란 원 체크 · 큰 제목 · 회색 설명 · 「처음으로」 */
export function Done({ title, children, onHome, top, button = '처음으로', tone }: { title: string; children: React.ReactNode; onHome: () => void; top?: React.ReactNode; button?: string | null; tone?: string }) {
  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '96px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16, boxSizing: 'border-box' }}>
      {top}
      <span style={{ width: 72, height: 72, borderRadius: '50%', background: tone ? X.card : X.blueTint, border: tone ? `2px solid ${tone}` : 'none', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={tone || X.blue} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="5 12.5 10 17.5 19 7.5" /></svg>
      </span>
      <h1 style={{ margin: '8px 0 0', fontSize: 26, fontWeight: 700, lineHeight: 1.35 }}>{title}</h1>
      <div style={{ margin: 0, fontSize: tone ? 18 : 16, color: tone ? X.label : X.sub, lineHeight: 1.6 }}>{children}</div>
      {button && <button type="button" onClick={onHome} style={{ marginTop: 24, height: 52, padding: '0 28px', border: '1.5px solid #D5D9DF', borderRadius: 14, background: X.card, color: X.text, fontFamily: 'inherit', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>{button}</button>}
    </div>
  );
}

/** 아래 고정 버튼 — 휴대폰: 바닥에 붙음 · 버튼 뒤는 바탕색으로 꽉 채우고 위 24px 만 흐리게(2026-10-13 5개 분야 검수: 흐린 띠가 「법인」 칸 · PC 입력칸을 가렸음)
 *  PC(768px 이상): 붙이지 않고 폼 끝에 그대로 · 아래 64px 여백(2026-10-14 지도사 「버튼이 바닥에 붙어서 안 보여」 — 창 바닥 · 작업 표시줄에 닿았음) · 버튼은 늘 채움(덜 채웠으면 글자만 「남은 칸 채우러 가기」 — 테두리 버튼은 보조처럼 보였음) */
export function BottomCta({ note, label, ready, busy, onClick, tone }: { note?: React.ReactNode; label: string; ready: boolean; busy: boolean; onClick: () => void; tone?: string }) {
  return (
    <div className="tax-cta" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20, background: `linear-gradient(rgba(243,244,246,0) 0, ${X.bg} 24px)`, padding: '28px 20px calc(16px + env(safe-area-inset-bottom))' }}>
      <style>{'@media (min-width:768px){.tax-cta{position:static!important;background:none!important;padding:28px 0 64px!important}}'}</style>
      <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {note && <p style={{ margin: 0, textAlign: 'center', fontSize: 15, color: X.label }}>{note}</p>}
        <button type="button" onClick={onClick} aria-busy={busy || undefined} data-ready={ready ? '1' : '0'} style={{
          height: 58, border: 'none', borderRadius: 16, fontFamily: 'inherit', fontSize: 18, fontWeight: 700, transition: 'background .2s',
          background: tone || X.blue, color: '#fff', cursor: busy ? 'progress' : 'pointer',
        }}>{label}</button>
      </div>
    </div>
  );
}

/** 마우스로 쓰는 화면(PC)인지 — 안내 글을 끌어다 놓기 · 카톡 저장 중 고른다 */
const finePointer = () => { try { return window.matchMedia('(pointer: fine)').matches; } catch { return false; } };
/** 파일 고르기(시안) — 이름 + 「n개 첨부 / 선택」 · 점선 상자(누르기 · 끌어다 놓기) · 줄마다 확장자 · 크기 · × */
export function FilePick({ label, files, max, onChange, big, tone, toneLt }: { label: string; files: File[]; max: number; onChange: (L: File[]) => void; big?: boolean; tone?: string; toneLt?: string }) {
  const ac = tone || X.blue, acLt = toneLt || X.blueLt;
  const [err, setErr] = React.useState('');
  const [drag, setDrag] = React.useState(false);
  const ref = React.useRef<HTMLInputElement>(null);
  const refPic = React.useRef<HTMLInputElement>(null);
  const add = (list: FileList | null) => {
    setErr('');
    if (!list) return;
    const next = files.slice();
    Array.from(list).forEach((f) => {
      if (f.size > 20 * MB) { setErr(`${f.name} — 20MB 이하 파일만 올릴 수 있어요`); return; }
      if (next.length >= max) { setErr(`${max}개까지 올릴 수 있어요`); return; }
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    });
    onChange(next);
  };
  const size = (b: number) => (b > MB ? (b / MB).toFixed(1) + 'MB' : Math.max(1, Math.round(b / 1024)) + 'KB');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: big ? 17 : 15, fontWeight: 600, color: X.text }}>{label}{big && <span style={{ fontWeight: 500, color: X.label, fontSize: 15 }}> (없어도 돼요)</span>}</span>
        {(!big || files.length > 0) && <span style={{ fontSize: big ? 15 : 13, fontWeight: 600, color: files.length ? ac : X.muted }}>{files.length ? `${files.length}개 첨부` : '선택'}</span>}
      </div>
      {/* 휴대폰(big) — 「사진 · 앨범」과 「파일」을 나눈다(2026-10-20 지도사 「카톡에서 받은 파일 저장해서 첨부하기가 어렵네」)
          사진이 섞인 accept 는 카톡 · 일부 휴대폰 브라우저가 앨범만 열어 PDF 가 안 보였다 → 「파일」은 accept 없이 = 문서 고르기(최근 · 다운로드 · KakaoTalk 폴더) */}
      {big && <input ref={refPic} type="file" multiple accept="image/*" onChange={(e) => { add(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />}
      <input ref={ref} type="file" multiple {...(big ? {} : { accept: ACCEPT })} onChange={(e) => { add(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />
      {big && (
        // 끌어다 놓기(2026-10-21 지도사 「파일은 드래그앤드랍으로 가능해야 해」) — 두 버튼 묶음 전체가 놓는 자리 · 끄는 동안 테두리 · 바탕을 키 색으로
        <div onDragOver={(e) => { e.preventDefault(); if (!drag) setDrag(true); }} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrag(false); }}
          onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, borderRadius: 16, outline: drag ? `2px solid ${ac}` : 'none', outlineOffset: 4, background: drag ? acLt : 'transparent', transition: 'background .15s' }}>
          {([['pic', '사진 · 앨범', refPic], ['doc', '파일 · PDF', ref]] as const).map(([k, t, r]) => (
            <button key={k} type="button" onClick={() => r.current && r.current.click()}
              style={{ fontFamily: 'inherit', cursor: 'pointer', minHeight: 56, padding: '0 12px', borderRadius: 14, border: `1.5px dashed ${drag ? ac : '#B8BEC7'}`, background: drag ? acLt : '#FAFBFC', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minWidth: 0 }}>
              <span aria-hidden style={{ flex: 'none', width: 26, height: 26, borderRadius: 8, background: tone ? acLt : X.blueTint, color: ac, fontSize: 18, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>+</span>
              <span style={{ fontSize: 16, fontWeight: 600, color: X.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t}</span>
            </button>
          ))}
        </div>
      )}
      {big && files.length === 0 && <span style={{ fontSize: 15, color: X.label, lineHeight: 1.5 }}>{drag ? '여기에 놓으면 올라가요' : finePointer() ? '파일을 여기로 끌어다 놓아도 돼요.' : '카톡으로 받은 파일은 카톡에서 저장한 뒤 「파일 · PDF」를 누르면 「최근」 맨 위에 있어요.'}</span>}
      {!big && <button type="button" onClick={() => ref.current && ref.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        style={{ fontFamily: 'inherit', cursor: 'pointer', padding: '18px 16px', borderRadius: 14, border: `1.5px dashed ${drag ? ac : big ? '#B8BEC7' : X.lineDk}`, background: drag ? acLt : '#FAFBFC', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'all .15s' }}>
        <span aria-hidden style={{ width: 28, height: 28, borderRadius: 9, background: tone ? acLt : X.blueTint, color: ac, fontSize: 19, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>+</span>
        <span style={{ fontSize: big ? 16 : 14, fontWeight: 600, color: X.text }}>{big ? '사진 · 파일 올리기' : '파일 선택'}</span>
        {!big && <span style={{ fontSize: 13, color: X.muted }}>또는 끌어다 놓기</span>}
      </button>}
      {err && <span role="alert" style={{ fontSize: big ? 15 : 13, fontWeight: big ? 600 : 400, color: big ? '#C42626' : X.red }}>{err}</span>}
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {files.map((f, i) => (
            <div key={f.name + f.size} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 12px 12px 14px', borderRadius: 12, background: '#F7F8FA' }}>
              <span aria-hidden style={{ flex: 'none', width: 40, height: 28, borderRadius: 6, background: X.card, border: `1px solid ${X.line}`, fontSize: 10, fontWeight: 700, color: X.label, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(f.name.split('.').pop() || '').toUpperCase().slice(0, 4)}</span>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: big ? 16 : 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <span style={{ fontSize: big ? 14 : 12, color: big ? X.label : X.muted }}>{size(f.size)}</span>
              </span>
              <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))} aria-label={`${f.name} 빼기`} style={{ flex: 'none', width: big ? 44 : 36, height: big ? 44 : 36, border: 'none', borderRadius: 8, background: 'transparent', color: X.muted, fontFamily: 'inherit', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
